import { Project, Repository, PresenceUser, ProjectMember } from '../../types.ts';
import {
  ApiError,
  fetchWithTimeout,
  parseErrorResponse,
  OfflineQueue,
  repositoryToServer,
  repositoryFromServer,
  BULK_FETCH_TIMEOUT,
  type ZelfMelden,
} from './base.ts';

export interface GroupedProjectsResponse {
  personal: Project[];
  organization: {
    id: string;
    name: string;
    slug: string;
    projects: Project[];
  } | null;
}

/**
 * Filter/sort opties voor `getProjects` (#843 Task 1.6).
 *
 * Allemaal optioneel — backwards-compat met de oude no-args call. Backend
 * accepteert deze als query-string parameters op `/api/projects`.
 */
export interface ProjectListOptions {
  status?: string;
  q?: string;
  client_id?: string;
  sort?:
    | 'name'
    | 'status'
    | 'updated_at'
    | 'expected_delivery_date'
    | 'client'
    | 'start_date'
    | 'expected_start_date';
  sort_dir?: 'asc' | 'desc';
  include_archived?: boolean;
  /**
   * @deprecated Doet niets en heeft nooit iets gedaan (#1225). De backend leest
   * deze parameter niet (`routes/projects.js:53` pakt alleen `include_archived`),
   * en de lijst-query filtert `deleted_at IS NULL` onvoorwaardelijk. De naam
   * suggereerde bovendien iets anders dan hij deed: 'deleted' en 'archived' zijn
   * twee verschillende dingen. Voor verwijderde projecten: `getDeletedProjects()`.
   */
  includeDeleted?: boolean;
}

/** Zacht-verwijderd project zoals `GET /api/projects/deleted` het teruggeeft (#1225). */
export interface DeletedProject {
  id: string;
  name: string;
  description: string | null;
  status: string | null;
  deleted_at: string;
  created_at: string;
}

/**
 * Payload voor `updateProject` — een `Partial<Project>` plus expliciete
 * ondersteuning voor `members` (functioneel project-rol per lid).
 */
export type ProjectUpdate = Partial<Project> & {
  members?: ProjectMember[];
};

export const projectsApi = {
  /**
   * Plan 3 (#778): canonical response-shape `{personal, organization}`. De
   * Array.isArray-fallback uit plan 1 is verwijderd. Componenten die nog een
   * flat array verwachten gebruiken `getProjectsFlat()` als bridge tot Phase G
   * groepering hen heeft gemigreerd.
   */
  getProjects: async (
    optsOrIncludeDeleted: ProjectListOptions | boolean = {}
  ): Promise<GroupedProjectsResponse> => {
    // Backwards-compat: oude call-sites geven `true`/`false` mee i.p.v. opts.
    const opts: ProjectListOptions =
      typeof optsOrIncludeDeleted === 'boolean'
        ? { includeDeleted: optsOrIncludeDeleted }
        : optsOrIncludeDeleted;

    const params = new URLSearchParams();
    if (opts.status) params.set('status', opts.status);
    if (opts.q) params.set('q', opts.q);
    if (opts.client_id) params.set('client_id', opts.client_id);
    if (opts.sort) params.set('sort', opts.sort);
    if (opts.sort_dir) params.set('sort_dir', opts.sort_dir);
    if (opts.include_archived) params.set('include_archived', 'true');
    if (opts.includeDeleted) params.set('includeDeleted', 'true');

    const qs = params.toString();
    const url = `/api/projects${qs ? `?${qs}` : ''}`;
    const res = await fetchWithTimeout(url);
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  /**
   * Backwards-compat helper voor componenten die nog niet zijn aangepast naar
   * de gegroepeerde weergave (Phase G). Combineert persoonlijke + organisatie-
   * projecten tot één flat array zoals de oude API teruggaf.
   */
  getProjectsFlat: async (
    optsOrIncludeDeleted: ProjectListOptions | boolean = {}
  ): Promise<Project[]> => {
    const data = await projectsApi.getProjects(optsOrIncludeDeleted as any);
    return [...data.personal, ...(data.organization?.projects ?? [])];
  },

  getProjectsOnline: async (): Promise<Record<string, number>> => {
    const res = await fetchWithTimeout('/api/projects/online');
    if (!res.ok) return {};
    return res.json();
  },

  createProject: async (
    name: string,
    options?: {
      description?: string;
      settings?: Record<string, unknown>;
      organizationId?: string | null;
      startDate?: string | null;
      expectedDeliveryDate?: string | null;
    }
  ): Promise<Project> => {
    const settings = {
      privacy: { redactYears: 100 },
      ...(options?.settings || {}),
    };
    // Refs #778 — Backend POST /api/projects accepteert organization_id
    // direct binnen dezelfde transactie. Geen follow-up PUT meer nodig
    // (dat patroon kon orphan-projecten achterlaten als de PUT faalde).
    // Refs Nextcloud #535 — startDate + expectedDeliveryDate ook al bij creatie
    // (DB-velden bestaan sinds #843, alleen body-mapping miste).
    const res = await fetchWithTimeout('/api/projects', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name,
        description: options?.description || '',
        settings,
        organization_id: options?.organizationId ?? null,
        start_date: options?.startDate ?? null,
        expected_delivery_date: options?.expectedDeliveryDate ?? null,
      }),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  updateProject: async (id: string, updates: ProjectUpdate, opts?: ZelfMelden): Promise<void> => {
    try {
      // #1435 — bij een taalwissel awaitet de PUT herschrijfPlaatsen (~2.4ms/rij);
      // bij ~2000 plaatsen breekt de standaard 5s FETCH_TIMEOUT dat clientside af.
      const res = await fetchWithTimeout(
        `/api/projects/${id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updates),
        },
        BULK_FETCH_TIMEOUT
      );
      if (!res.ok) await parseErrorResponse(res, opts);
    } catch (error) {
      // #1361 — de queue is er voor een onbereikbare server, niet voor een
      // server die antwoordt met een fout. Zo'n PUT opnieuw aanbieden levert
      // dezelfde 4xx/5xx op, en zolang hij hier werd opgeslokt keerde de
      // aanroep normaal terug: de import meldde 'geslaagd' terwijl de proband
      // nooit was vastgelegd.
      if (error instanceof ApiError) throw error;
      console.warn('⚠️ Database unavailable, queuing operation:', error);
      OfflineQueue.enqueue({
        operation: 'UPDATE',
        endpoint: `/api/projects/${id}`,
        method: 'PUT',
        data: updates,
      });
    }
  },

  /**
   * Zacht-verwijderde projecten van de ingelogde eigenaar (#1225). Zonder deze
   * lijst was er geen weg terug: het project staat in geen enkele andere lijst,
   * dus de gebruiker kwam niet aan het ID dat `restoreProject` nodig heeft.
   */
  getDeletedProjects: async (): Promise<DeletedProject[]> => {
    const res = await fetchWithTimeout('/api/projects/deleted');
    if (!res.ok) await parseErrorResponse(res);
    const data = await res.json();
    return data.projects || [];
  },

  restoreProject: async (id: string): Promise<void> => {
    try {
      const res = await fetchWithTimeout(`/api/projects/${id}/restore`, {
        method: 'PATCH',
      });
      if (!res.ok) await parseErrorResponse(res);
    } catch (error) {
      // #1361 — zelfde patroon als updateProject: een serverfout hoort niet in
      // de offline-queue.
      if (error instanceof ApiError) throw error;
      console.warn('⚠️ Database unavailable, queuing operation:', error);
      OfflineQueue.enqueue({
        operation: 'UPDATE',
        endpoint: `/api/projects/${id}/restore`,
        method: 'PATCH',
        data: {},
      });
    }
  },

  softDeleteProject: async (id: string): Promise<void> => {
    const res = await fetchWithTimeout(`/api/projects/${id}`, { method: 'DELETE' });
    if (!res.ok) await parseErrorResponse(res);
  },

  hardPurgeProject: async (id: string): Promise<void> => {
    try {
      const res = await fetchWithTimeout(`/api/projects/${id}/purge`, {
        method: 'DELETE',
      });
      if (!res.ok) await parseErrorResponse(res);
    } catch (error) {
      console.warn('⚠️ Database unavailable, queuing operation:', error);
      OfflineQueue.enqueue({
        operation: 'DELETE',
        endpoint: `/api/projects/${id}/purge`,
        method: 'DELETE',
        data: {},
      });
    }
  },

  getRepositories: async (projectId: string): Promise<Repository[]> => {
    try {
      const res = await fetchWithTimeout(`/api/projects/${projectId}/repositories`);
      if (!res.ok) await parseErrorResponse(res);
      const rows = await res.json();
      return rows.map(repositoryFromServer);
    } catch (error) {
      console.warn('⚠️ Database unavailable, returning empty repositories list:', error);
      return [];
    }
  },

  saveRepository: async (repo: Partial<Repository>): Promise<Repository> => {
    const isUpdate = !!repo.id;
    const serverPayload = repositoryToServer(repo);

    try {
      if (isUpdate) {
        const res = await fetchWithTimeout(`/api/repositories/${repo.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(serverPayload),
        });
        if (!res.ok) await parseErrorResponse(res);
        return repositoryFromServer(await res.json());
      } else {
        const res = await fetchWithTimeout(`/api/projects/${repo.projectId}/repositories`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(serverPayload),
        });
        if (!res.ok) await parseErrorResponse(res);
        return repositoryFromServer(await res.json());
      }
    } catch (error) {
      // #1359-familie — dit was het gevaarlijkste exemplaar. Bij een serverfout
      // gaf deze functie een archief terug met een verzonnen `randomUUID()` als
      // id. De GEDCOM-import zette dat id in zijn koppelkaart en gaf het door
      // als `repository_id` van elke bijbehorende bron; de foreign key
      // `sources_repository_id_fkey` bestaat niet voor dat id, dus de
      // bronnen-bulk viel om met een 500 en nam de héle import mee. Eén
      // haperend archief kostte zo de complete import — en bij een import in
      // een bestaand project blijft die helft permanent staan.
      if (error instanceof ApiError) throw error;
      console.warn('⚠️ Database unavailable, queuing operation:', error);
      const tempId = repo.id || crypto.randomUUID();
      OfflineQueue.enqueue({
        operation: isUpdate ? 'UPDATE' : 'CREATE',
        endpoint: isUpdate
          ? `/api/repositories/${repo.id}`
          : `/api/projects/${repo.projectId}/repositories`,
        method: isUpdate ? 'PUT' : 'POST',
        data: repo,
      });
      return {
        ...repo,
        id: tempId,
        _offline: true,
      } as any;
    }
  },

  deleteRepository: async (id: string): Promise<void> => {
    try {
      const res = await fetchWithTimeout(`/api/repositories/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) await parseErrorResponse(res);
    } catch (error) {
      console.warn('⚠️ Database unavailable, queuing operation:', error);
      OfflineQueue.enqueue({
        operation: 'DELETE',
        endpoint: `/api/repositories/${id}`,
        method: 'DELETE',
        data: {},
      });
    }
  },

  getPresence: async (projectId: string): Promise<PresenceUser[]> => {
    try {
      const res = await fetchWithTimeout(`/api/projects/${projectId}/presence`);
      if (!res.ok) await parseErrorResponse(res);
      return res.json();
    } catch (error) {
      console.warn('⚠️ Presence endpoint unavailable, using fallback:', error);
      const user = JSON.parse(localStorage.getItem('af_user') || '{}');
      return [
        {
          id: user.id || 'me',
          email: user.email || 'You',
          lastActive: new Date().toISOString(),
          isMe: true,
        },
      ];
    }
  },

  getProjectActivity: async (projectId: string): Promise<any[]> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/activity`);
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  getProjectFactKeys: async (projectId: string): Promise<string[]> => {
    try {
      const res = await fetchWithTimeout(`/api/projects/${projectId}/fact-keys`);
      if (!res.ok) return [];
      return res.json();
    } catch {
      return [];
    }
  },
};
